/**
 * User Service
 * Responsabilidad: Gestión de documentos de usuario en Firestore
 *
 * Separado de auth.service.ts para cumplir SRP:
 * - auth.service.ts: Solo autenticación Firebase
 * - userService.ts: Solo gestión de documentos de usuario
 */

import type { User } from 'firebase/auth'
import { doc, getDoc, getDocFromServer, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase/config'
import { deriveDisplayName } from '@/utils/displayName'

/**
 * App en la que se creó la cuenta: `professor` = este admin, `student` = app del
 * estudiante o portal. **No autoriza nada** (ADR-SEC-020): el profesor es quien
 * tiene `professor_profiles/{uid}`.
 */
export type UserRole = 'professor' | 'student'

export interface UserDocument {
  uid: string
  email: string
  role: UserRole
  display_name: string
  photo_url: string | null
  is_active: boolean
  email_verified: boolean
  created_at: ReturnType<typeof serverTimestamp>
  created_by: string
  updated_at: ReturnType<typeof serverTimestamp>
  updated_by: string
}

/**
 * Perfil de profesor tal como nace en el registro.
 * Contrato: `specs/API-CONTRACT/professor-profiles.md` v3.
 */
export interface ProfessorProfileDocument {
  professor_id: string
  /**
   * **Réplica desnormalizada de `users.display_name`** (contrato v2).
   *
   * Se llama igual que su origen a propósito — la convención del ecosistema
   * nombra los campos desnormalizados por su **contenido**, no por su
   * procedencia (`classroom_students.student_name`). Existe para que la bandeja
   * del backoffice no lea `users` por fila.
   */
  display_name: string
  /**
   * Espejo del correo del token (contrato v3, 2026-10-08). La regla exige
   * `email == request.auth.token.email` al crear, y es inmutable después: existe
   * para que el backoffice busque al profesor en `professor_profiles` sin pasar
   * por `users.role`, que ya no dice quién es profesor.
   */
  email: string
  phone: string | null
  timezone: string
  language: string
  account_status: 'pending'
  exam_credits_balance: 0
  activated_at: null
  activated_by: null
  pioneer_until: null
  suspended_at: null
  suspended_by: null
  suspension_reason: null
  created_at: ReturnType<typeof serverTimestamp>
  created_by: string
  updated_at: ReturnType<typeof serverTimestamp>
  updated_by: string
}

export const userService = {
  /**
   * Crear documento de usuario en Firestore.
   *
   * `displayName` es el nombre **ya derivado** con la regla del ecosistema
   * (ADR-023): quien aprovisiona lo deriva una vez y pasa el mismo valor a este
   * documento, al espejo de `professor_profiles` y a Firebase Auth. Si no llega, se
   * deriva aquí del `displayName` y el correo de la cuenta — nunca `''`.
   *
   * (2026-11-02, DEBT-SPA-04 parcial) No se registra el documento en consola: lleva
   * PII (correo, nombre, foto). Sólo el `uid`.
   */
  async createUserDocument(
    user: User,
    role: UserRole = 'professor',
    displayName: string = deriveDisplayName(user.displayName, user.email)
  ): Promise<void> {
    try {
      const userDoc: UserDocument = {
        uid: user.uid,
        email: user.email || '',
        role: role,
        display_name: displayName,
        photo_url: user.photoURL || null,
        is_active: true,
        email_verified: user.emailVerified,
        created_at: serverTimestamp(),
        created_by: user.uid,
        updated_at: serverTimestamp(),
        updated_by: user.uid,
      }

      await setDoc(doc(db, 'users', user.uid), userDoc)

      console.log(`Documento de usuario creado (uid ${user.uid}, role ${role})`)
    } catch (error) {
      console.error('❌ Error al crear documento de usuario:', error)
      throw error
    }
  },

  /**
   * Verificar si un documento de usuario existe
   */
  async userDocumentExists(userId: string): Promise<boolean> {
    const userDoc = await getDoc(doc(db, 'users', userId))
    return userDoc.exists()
  },

  /**
   * Asegurar que existe un documento de usuario (crear si no existe).
   * Útil para usuarios que se autentican por primera vez.
   *
   * Devuelve `true` si **esta** llamada creó el documento: el aprovisionamiento
   * sólo sincroniza el nombre a Firebase Auth cuando nació aquí (ADR-023), nunca
   * sobre una cuenta que ya tenía su nombre en Firestore.
   */
  async ensureUserDocument(
    user: User,
    role: UserRole = 'professor',
    displayName?: string
  ): Promise<boolean> {
    const exists = await this.userDocumentExists(user.uid)

    if (!exists) {
      await this.createUserDocument(user, role, displayName)
      return true
    }
    return false
  },

  /**
   * Crear el perfil de profesor si no existe — **pendiente de activación**.
   *
   * HU-AUTH-001 declara esta escritura en su CRUD matrix **desde el bootstrap**,
   * y hasta el 2026-09-08 NINGÚN código del repo la ejecutaba: el registro creaba
   * `users` y nada más. Se destapó al cablear el gate de apertura de aulas, que
   * exige que el perfil exista — sin esto, ningún profesor nuevo habría podido
   * abrir un aula. Sexta aparición de «una regla documentada que nadie ejecuta no
   * es una regla».
   *
   * La cuenta nace `pending` con saldo 0, y la regla de `professor_profiles`
   * lo **exige**: no se puede nacer activo ni con crédito. La activación la hace
   * un operador desde `quizoteca-backoffice` (§3.7 de la decisión de precios).
   *
   * Idempotente: si el perfil ya existe no lo toca — reescribirlo pisaría el
   * `account_status` que el operador acaba de fijar.
   *
   * **Desde el 2026-10-08 (ADR-SEC-020) tener este documento es lo que hace
   * profesor a una cuenta**, y la SPA lo asegura también al iniciar o restaurar
   * sesión (`EnsureProfessorProfileGuard`), después del consentimiento: así una
   * cuenta nacida en la app del estudiante (`users.role == 'student'`) se vuelve
   * profesora con solo entrar al admin. La existencia se lee **del servidor**
   * (ADR-021): decide una escritura, y una caché vacía se leería como «no existe».
   */
  async ensureProfessorProfile(
    user: User,
    displayName: string = deriveDisplayName(user.displayName, user.email)
  ): Promise<void> {
    const ref = doc(db, 'professor_profiles', user.uid)
    const existing = await getDocFromServer(ref)
    if (existing.exists()) return

    const profile: ProfessorProfileDocument = {
      professor_id: user.uid,
      // Espejo de `users.display_name`: el MISMO valor derivado que el
      // aprovisionamiento acaba de escribir allí (ADR-023), que es lo que hace de
      // este campo un espejo y no un segundo nombre. La derivación nunca da vacío,
      // así que la regla (`display_name` no vacío) se cumple por construcción.
      // Hasta el 2026-11-02 era `displayName || email || 'Profesor'` mientras
      // `users` recibía `displayName || ''`: con Google sin nombre, el espejo y su
      // original nacían distintos.
      display_name: displayName,
      // El del token: la regla lo compara con `request.auth.token.email`.
      email: user.email || '',
      phone: null,
      timezone: 'America/Panama',
      language: 'es',
      account_status: 'pending',
      exam_credits_balance: 0,
      activated_at: null,
      activated_by: null,
      pioneer_until: null,
      suspended_at: null,
      suspended_by: null,
      suspension_reason: null,
      created_at: serverTimestamp(),
      created_by: user.uid,
      updated_at: serverTimestamp(),
      updated_by: user.uid,
    }

    await setDoc(ref, profile)
    console.log('✅ Perfil de profesor creado (pendiente de activación)')
  },

  /**
   * Obtener documento de usuario
   */
  async getUserDocument(userId: string): Promise<UserDocument | null> {
    const userDoc = await getDoc(doc(db, 'users', userId))

    if (!userDoc.exists()) {
      return null
    }

    return userDoc.data() as UserDocument
  },
}
